import { createRoot } from "react-dom/client";
import { App } from "./App";
import { TileLibrary } from "./library/TileLibrary";
import { FortressLibrary } from "./library/FortressLibrary";
import { FortressEditor } from "./library/FortressEditor";
import { CardsEditor } from "./library/CardsEditor";
import { AuthPage } from "./auth/AuthPage";
import { ChooseUsernamePage } from "./auth/ChooseUsernamePage";
import { ensureValidSession } from "./auth/session";
import { createSandbox, createTutorial } from "./auth/lobbies";
import { matchPath } from "./matchPath";
import { getAppState } from "./appState";
import { registerPushNotifications } from "./push";
import "./styles.css";
import "./hub.css";

const push = true;
const container = document.getElementById("root");
if (!container) throw new Error('Root element "#root" not found');
const root = createRoot(container);

// Tiny path-based routing — no router dep. `/auth` is the login screen and
// `/auth/username` the post-login handle picker; those are the only public
// paths. Everything else sits behind the auth gate: the hub tabs (`/`,
// `/lobbies`, `/matches`, `/pools`, `/account`), `/match/:gameId` deep links,
// and the `/library/*` + `/edit/cards` catalogue tools. Unauthenticated visits
// keep `returnTo` so login lands back on the same page; Colyseus `onAuth`
// still gates the actual seat/spectator join.
const path = window.location.pathname;

function redirectToAuth(screen: "/auth" | "/auth/username"): void {
  const returnTo = encodeURIComponent(path + window.location.search);
  window.location.replace(`${screen}?returnTo=${returnTo}`);
}

async function boot(): Promise<void> {
  if (path === "/auth/username" || path.startsWith("/auth/username/")) {
    const session = await ensureValidSession();
    if (!session) {
      const returnTo = encodeURIComponent(
        new URLSearchParams(window.location.search).get("returnTo") || "/",
      );
      window.location.replace(`/auth?returnTo=${returnTo}`);
      return;
    }
    if (session.user.username) {
      const param = new URLSearchParams(window.location.search).get("returnTo");
      const dest =
        param && param.startsWith("/") && !param.startsWith("//") ? param : "/";
      window.location.replace(dest);
      return;
    }
    root.render(<ChooseUsernamePage />);
    return;
  }

  if (path.startsWith("/auth")) {
    root.render(<AuthPage />);
    return;
  }

  // Server-driven gate: ask /app-state, then validate any stored JWT pair via
  // GET /auth/me (user must still exist). No usable session → /auth (email
  // OTP); no username yet → the picker. AUTH_DISABLED skips the gate for
  // local guest play only.
  const { authDisabled, tutorialEnabled } = await getAppState();
  const tutorialOn = tutorialEnabled === true;
  let selfUserId: string | undefined;
  // "Complete" also when the tutorial is switched off: nothing offers it.
  let tutorialComplete = true;
  if (!authDisabled) {
    const session = await ensureValidSession();
    if (!session) {
      redirectToAuth("/auth");
      return;
    }
    if (!session.user.username) {
      redirectToAuth("/auth/username");
      return;
    }
    selfUserId = session.user.userId;
    tutorialComplete = !tutorialOn || session.user.tutorialComplete === true;
    // Native app: record this device's push token on the account (fail-soft,
    // fire-and-forget — a no-op on web builds).
    void registerPushNotifications();
  }

  // `/sandbox`: mint a private, never-saved solo match that is already
  // under way, then enter it like any match — a way to see the in-game UI
  // without arranging a real game. Errors stay on this page (the splash
  // would otherwise sit there forever).
  if (path === "/sandbox" || path === "/sandbox/") {
    try {
      const created = await createSandbox();
      window.location.replace(matchPath(created.gameId));
    } catch (err) {
      root.render(
        <p style={{ padding: 24 }}>
          Couldn&apos;t open a sandbox:{" "}
          {err instanceof Error ? err.message : String(err)}
        </p>,
      );
    }
    return;
  }

  // `/tutorial`: mint the tutorial match (a scripted lesson against a bot
  // rival) and enter it like any match. Off (server flag) → the hub.
  if (path === "/tutorial" || path === "/tutorial/") {
    if (!tutorialOn) {
      window.location.replace("/");
      return;
    }
    try {
      const created = await createTutorial();
      window.location.replace(matchPath(created.gameId));
    } catch (err) {
      root.render(
        <p style={{ padding: 24 }}>
          Couldn&apos;t open the tutorial:{" "}
          {err instanceof Error ? err.message : String(err)}
        </p>,
      );
    }
    return;
  }

  if (path.startsWith("/library/tiles")) {
    root.render(<TileLibrary />);
    return;
  }
  if (path.startsWith("/library/fortresses/edit")) {
    root.render(<FortressEditor />);
    return;
  }
  if (path.startsWith("/library/fortresses")) {
    root.render(<FortressLibrary />);
    return;
  }
  if (path.startsWith("/edit/cards")) {
    root.render(<CardsEditor />);
    return;
  }

  // NOTE: React.StrictMode is intentionally omitted. Its double-invocation of
  // effects in development conflicts with PixiJS's async Application.init()
  // lifecycle, which would otherwise create two canvases on mount.
  root.render(
    authDisabled ? (
      <App />
    ) : (
      <App
        authenticated
        selfUserId={selfUserId}
        tutorialComplete={tutorialComplete}
      />
    ),
  );
}

void boot();
